import { ref } from 'vue'
import { defineStore } from 'pinia'
import { Gender, Age } from '../composables/constants'
import {
  Insights,
  StandaloneNativePackGenerator,
  StandaloneNativePackGeneratorInput,
  StandaloneReorderHandlesAudienceUsedInAdsInput,
} from '@/api'
import { LabeledValueWithOptions } from '@/base'

const STORE_KEY = 'protargeting-autopilot'

export const useAutopilotStore = defineStore(STORE_KEY, () => {
  const loading = ref(false)
  const createAudienceOption = ref<'description' | 'finetune'>('description')
  const keywordGroups = ref<string[][]>([[]])
  const statesSelected = ref<string[]>([])
  const excludeKeywords = ref<string[]>([])
  const handles = ref<string[]>([])
  const regionsSelected = ref<string[]>([])
  const order = ref<Exclude<StandaloneReorderHandlesAudienceUsedInAdsInput['order'], 'custom'>>('balanced')
  const gender = ref<Gender>('ANY')
  const age = ref<Age>('AGE_ANY')
  const description = ref('')
  const lastUsedDescription = ref('')
  const previewData = ref<StandaloneNativePackGenerator>()
  const targetingPackageExtra = ref<Record<string, any>>({})
  const error = ref<string>('')
  const processingTargetingList = ref(false)
  const insights = ref<Insights>()
  const budget = ref()
  const size = ref()
  const sizeOption = ref<'size' | 'budget'>('budget')

  const showHelp = ref(false)

  const budgetSuggested = ref()
  const sizeSuggested = ref()

  const mappedBudgetOptions = ref<LabeledValueWithOptions<StandaloneNativePackGeneratorInput['input_budget']>[]>([
    {
      label: '$500',
      value: 500,
    },
    {
      label: '$1,000',
      value: 1000,
    },
    {
      label: '$2,500',
      value: 2500,
    },
    {
      label: '$5,000',
      value: 5000,
    },
    {
      label: '$10,000',
      value: 10000,
    },
    {
      label: '$20,000',
      value: 20000,
    },
    {
      label: '$50,000',
      value: 50000,
    },
    {
      label: '$100,000',
      value: 100000,
    },
    {
      label: '$250,000',
      value: 250000,
    },
    {
      label: '$500,000',
      value: 500000,
    },
    {
      label: '$750,000',
      value: 750000,
    },
    {
      label: '$1,000,000',
      value: 1000000,
    },
    {
      label: 'I will let Socialdatabase decide',
      value: undefined,
    },
  ])

  const mappedSizeOptions = ref<LabeledValueWithOptions<StandaloneNativePackGeneratorInput['input_size']>[]>([
    {
      label: '25,000',
      value: 25000,
    },
    {
      label: '50,000',
      value: 50000,
    },
    {
      label: '100,000',
      value: 100000,
    },
    {
      label: '150,000',
      value: 150000,
    },
    {
      label: '300,000',
      value: 300000,
    },
    {
      label: '600,000',
      value: 600000,
    },
    {
      label: '1,500,000',
      value: 1500000,
    },
    {
      label: '3,000,000',
      value: 3000000,
    },
    {
      label: '7,500,000',
      value: 7500000,
    },
    {
      label: '15,000,000',
      value: 15000000,
    },
    {
      label: '22,000,000',
      value: 22000000,
    },
    {
      label: '30,000,000',
      value: 30000000,
    },
    {
      label: 'I will let Socialdatabase decide',
      value: undefined,
    },
  ])

  return {
    showHelp,
    loading,
    createAudienceOption,
    keywordGroups,
    statesSelected,
    excludeKeywords,
    handles,
    regionsSelected,
    order,
    gender,
    age,
    description,
    lastUsedDescription,
    previewData,
    targetingPackageExtra,
    error,
    processingTargetingList,
    insights,
    sizeOption,
    budget,
    size,
    budgetSuggested,
    sizeSuggested,
    mappedBudgetOptions,
    mappedSizeOptions,
  }
})
